const SSO_PROVIDER_ID = /^(oidc|saml)\.[A-Za-z0-9_-]+$/;
/** Whether a Firebase provider ID (or `sign_in_provider`) is an SSO one. */
export const isSsoProviderId = (providerId) => typeof providerId === 'string' && SSO_PROVIDER_ID.test(providerId);
/** `saml` or `oidc` for an SSO provider ID, otherwise null. */
export const getSsoProviderKind = (providerId) => isSsoProviderId(providerId)
    ? providerId.slice(0, providerId.indexOf('.'))
    : null;
export const GOOGLE_PROVIDER_ID = 'google.com';
/** The kind of Firebase provider a team's single sign-on uses. */
export const getSsoProviderType = (provider) => {
    if (provider === 'google-workspace')
        return 'google';
    return provider === 'okta-saml' ? 'saml' : 'oidc';
};
export const DEFAULT_SSO_POLICY = {
    accessTokenMaxDays: 90,
    accessTokenRevokeOnRemoval: true,
    joinRequestEmails: true,
    joinRequests: true,
    ownerEmergencyAccess: true,
    required: false,
    sessionMaxHours: 12,
};
export const SSO_SESSION_MAX_HOURS_OPTIONS = [12, 24, 168];
export const SSO_ACCESS_TOKEN_MAX_DAYS_OPTIONS = [30, 90, 365];
export const ssoVerificationRecordName = (domain) => `_todesktop-verification.${domain}`;
export const ssoVerificationRecordValue = (token) => `todesktop-verification=${token}`;
const DOMAIN = /^(?=.{4,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;
/** A domain as people type it (`@Acme.com `), or null when it isn't one. */
export const normalizeSsoDomain = (input) => {
    if (typeof input !== 'string')
        return null;
    const domain = input
        .trim()
        .toLowerCase()
        .replace(/^@/, '')
        .replace(/\.$/, '');
    return DOMAIN.test(domain) ? domain : null;
};
/** The domain of an email address, normalized, or null. */
export const getEmailDomain = (email) => typeof email === 'string' && email.includes('@')
    ? normalizeSsoDomain(email.slice(email.lastIndexOf('@') + 1))
    : null;
/**
 * The custom claim on the session an emailed SSO link signs in with. That
 * session exists only to connect the team's provider to the account, so
 * rules, functions and the API refuse it everything else.
 */
export const SSO_LINK_ONLY_CLAIM = 'ssoLinkOnly';
/** Whether decoded token claims are an SSO link's connect-only session. */
export const isSsoLinkOnlySession = (claims) => Boolean(claims) &&
    typeof claims === 'object' &&
    claims[SSO_LINK_ONLY_CLAIM] === true;
/**
 * The session claim for when a sign-in at a team's domain ends, in seconds
 * since the epoch: the team's "sign members out after" (beforeSignIn sets
 * it). Rules, functions and the API refuse the session after that, and the
 * dashboard signs out.
 */
export const SSO_SESSION_ENDS_CLAIM = 'ssoSessionEnds';
/**
 * Why a session can't be used, or null: an emailed SSO link's
 * connect-only session, or a sign-in past its team's session length.
 */
export const restrictedSessionReason = (claims, now = Date.now()) => {
    if (isSsoLinkOnlySession(claims))
        return 'sso-link-only';
    const ends = claims && typeof claims === 'object'
        ? claims[SSO_SESSION_ENDS_CLAIM]
        : undefined;
    return typeof ends === 'number' && now >= ends * 1000
        ? 'sso-session-ended'
        : null;
};
/** Where single sign-on data lives. Only Cloud Functions write it. */
export const ssoPaths = {
    accountDomain: (accountId, domain) => `users/${accountId}/ssoDomains/${domain}`,
    accountDomains: (accountId) => `users/${accountId}/ssoDomains`,
    domainClaim: (domain) => `ssoDomains/${domain}`,
    domainClaims: 'ssoDomains',
    joinRequest: (accountId, uid) => `users/${accountId}/ssoJoinRequests/${uid}`,
    joinRequests: (accountId) => `users/${accountId}/ssoJoinRequests`,
    settings: (accountId) => `users/${accountId}/sso/settings`,
};
